/* ============================================================================
 * AccountLifecycleCard — Settings › Account (owner only).
 *
 * Cancel in a few clicks, no forms (terms §8). Opt in to keep only the PQ
 * profile for 12 months, default off (§10). Reactivate within the 90 days.
 * Delete now (§11), with the export offered once first (§9).
 * The database enforces all of it (migrations 110, 111); this card explains it.
 * ========================================================================== */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UserCog, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  cancelAccount,
  deleteAccountNow,
  isCancelling,
  reactivateAccount,
  setKeepPqProfile,
} from '@/lib/accountData';
import { useAuth } from '@/context/AuthContext';
import { ExportPanel } from '@/components/settings/AccountExportCard';

const fmtDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const AccountLifecycleCard = ({ state, onChange, exp, setExp }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [keepProfile, setKeepProfile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState('offer'); // offer the export once, then confirm
  const [typed, setTyped] = useState('');

  if (!state) return null;
  const cancelling = isCancelling(state);
  const readOnly = state.status === 'read_only';
  const deletedKept = state.status === 'deleted';
  const active = state.status === 'active' && !cancelling;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const next = await fn();
      if (next) onChange?.(next);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(t(`account.err_${e.code}`, { defaultValue: e.message || t('account.err_generic') }));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = () =>
    run(() => cancelAccount(keepProfile), t('account.cancelled_toast')).then(() =>
      setConfirmOpen(false)
    );

  const canDelete = active || cancelling || readOnly;
  const openDelete = () => {
    setDeleteStep('offer');
    setTyped('');
    setDeleteOpen(true);
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteAccountNow();
      toast.success(t('account.deleted_toast'));
      setDeleteOpen(false);
      await signOut();
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(t(`account.err_${e.code}`, { defaultValue: e.message || t('account.err_generic') }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="account-card">
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 flex items-center justify-center shrink-0">
            <UserCog size={16} className="text-[hsl(var(--primary))]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('account.eyebrow')}
            </div>
            <div className="text-base font-semibold text-foreground tracking-tight">
              {t('account.title')}
            </div>
          </div>
        </div>

        {/* Where the account stands */}
        <div className="text-sm text-foreground space-y-2" data-testid="account-status">
          {active ? <p>{t('account.active_body')}</p> : null}
          {cancelling ? (
            <p>
              {t('account.cancelling_body', {
                until: fmtDate(state.access_until, lang),
              })}
            </p>
          ) : null}
          {readOnly ? (
            <p>
              {state.cause === 'payment_failed'
                ? t('account.read_only_payment_body', { date: fmtDate(state.delete_after, lang) })
                : t('account.read_only_body', { date: fmtDate(state.delete_after, lang) })}
            </p>
          ) : null}
          {deletedKept ? (
            <p>{t('account.deleted_kept_body', { date: fmtDate(state.pq_profile_expires_at, lang) })}</p>
          ) : null}
        </div>

        {/* PQ profile choice while leaving */}
        {cancelling || readOnly ? (
          <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">{t('account.keep_profile_label')}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('account.keep_profile_help')}</div>
            </div>
            <Switch
              checked={!!state.keep_pq_profile}
              disabled={busy}
              onCheckedChange={(v) => run(() => setKeepPqProfile(v))}
              aria-label={t('account.keep_profile_label')}
              data-testid="account-keep-profile-switch"
            />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {active ? (
            <Button
              variant="outline"
              className="border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))]"
              onClick={() => {
                setKeepProfile(false);
                setConfirmOpen(true);
              }}
              data-testid="account-cancel-btn"
            >
              {t('account.cancel_btn')}
            </Button>
          ) : null}
          {cancelling || readOnly || deletedKept ? (
            <Button
              onClick={() => run(reactivateAccount, t('account.reactivated_toast'))}
              disabled={busy}
              data-testid="account-reactivate-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {deletedKept ? t('account.restart_btn') : t('account.reactivate_btn')}
            </Button>
          ) : null}
        </div>

        {canDelete ? (
          <div className="border-t border-border pt-4 space-y-2">
            <div className="text-sm font-medium text-foreground">{t('account.delete_title')}</div>
            <p className="text-xs text-muted-foreground">{t('account.delete_help')}</p>
            <Button
              variant="outline"
              size="sm"
              className="border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))]"
              onClick={openDelete}
              data-testid="account-delete-btn"
            >
              {t('account.delete_btn')}
            </Button>
          </div>
        ) : null}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="account-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('account.confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  {t('account.confirm_access', {
                    until: fmtDate(state.paid_through, lang),
                  })}
                </p>
                <p>{t('account.confirm_read_only')}</p>
                <p>{t('account.confirm_insights')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer">
            <Checkbox
              checked={keepProfile}
              onCheckedChange={(v) => setKeepProfile(!!v)}
              data-testid="account-cancel-keep-profile"
            />
            <span className="text-sm">
              <span className="font-medium text-foreground">{t('account.keep_profile_label')}</span>
              <span className="block text-xs text-muted-foreground mt-0.5">{t('account.keep_profile_help')}</span>
            </span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t('account.keep_account_btn')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doCancel();
              }}
              disabled={busy}
              className="bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90"
              data-testid="account-cancel-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t('account.cancel_confirm_btn')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={(o) => !busy && setDeleteOpen(o)}>
        <AlertDialogContent data-testid="account-delete-dialog">
          {deleteStep === 'offer' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('account.delete_offer_title')}</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>{t('account.delete_offer_body')}</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <ExportPanel exp={exp} setExp={setExp} compact />
              <AlertDialogFooter>
                <AlertDialogCancel>{t('account.keep_account_btn')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    setDeleteStep('confirm');
                  }}
                  className="bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90"
                  data-testid="account-delete-continue"
                >
                  {t('account.delete_continue_btn')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('account.delete_confirm_title')}</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>{t('account.delete_confirm_body')}</p>
                    <p>{t('account.delete_confirm_type')}</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="DELETE"
                autoCapitalize="characters"
                autoComplete="off"
                aria-label={t('account.delete_confirm_type')}
                data-testid="account-delete-input"
              />
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>{t('account.keep_account_btn')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => {
                    e.preventDefault();
                    doDelete();
                  }}
                  disabled={busy || typed.trim() !== 'DELETE'}
                  className="bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90"
                  data-testid="account-delete-confirm"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t('account.delete_confirm_btn')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default AccountLifecycleCard;
